import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Code, Sparkles, ChevronDown, ChevronUp, Copy, Plus, Info,
  Search, Variable
} from 'lucide-react';
import { type VariableCatalog } from '../api';

interface TemplateVariablesDrawerProps {
  variables: VariableCatalog | undefined;
  onInsertToken: (token: string) => void;
}

export function TemplateVariablesDrawer({
  variables,
  onInsertToken,
}: TemplateVariablesDrawerProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'doc' | 'branding'>('all');

  const docTokens = variables?.docTokens || [];
  const brandingTokens = variables?.brandingTokens || [];

  const filterToken = (t: { token: string; label: string }) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return t.token.toLowerCase().includes(q) || t.label.toLowerCase().includes(q);
  };

  const displayedDocTokens = docTokens.filter(filterToken);
  const displayedBrandingTokens = brandingTokens.filter(filterToken);

  const totalTokens = docTokens.length + brandingTokens.length;

  function handleInsert(token: string) {
    onInsertToken(token);
    toast.success(`Inserted {{${token}}}`);
  }

  function handleCopy(e: React.MouseEvent, token: string) {
    e.stopPropagation();
    navigator.clipboard.writeText(`{{${token}}}`);
    toast.success(`Copied {{${token}}} to clipboard`);
  }

  return (
    <div className="rounded-xl border border-outline bg-surface shadow-sm overflow-hidden transition-all">
      {/* Drawer Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between px-4 py-3 bg-surface-variant/30 cursor-pointer hover:bg-surface-variant/50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Variable className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-on-surface flex items-center gap-2">
              <span>Template Variable Tokens</span>
              <span className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-medium text-muted-foreground border border-outline">
                {totalTokens} available
              </span>
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Click any badge to insert into your document, or copy token Handlebars tag.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="p-1 rounded-md text-muted-foreground hover:text-on-surface hover:bg-surface"
          aria-label={isExpanded ? 'Collapse tokens drawer' : 'Expand tokens drawer'}
        >
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded Token Palettes */}
      {isExpanded && (
        <div className="p-4 border-t border-outline/50 space-y-4">
          {/* Controls Bar: Tabs & Search */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                  activeTab === 'all'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'bg-surface-variant/50 text-on-surface-variant hover:bg-surface-variant'
                }`}
              >
                All Tokens ({totalTokens})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('doc')}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                  activeTab === 'doc'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'bg-surface-variant/50 text-on-surface-variant hover:bg-surface-variant'
                }`}
              >
                Document Data ({docTokens.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('branding')}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                  activeTab === 'branding'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'bg-surface-variant/50 text-on-surface-variant hover:bg-surface-variant'
                }`}
              >
                Company Branding ({brandingTokens.length})
              </button>
            </div>

            {/* Filter Search */}
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter token name..."
                className="w-full h-7 rounded-md border border-outline bg-surface-variant/30 pl-8 pr-3 text-xs text-on-surface placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Tokens Grid */}
          <div className="space-y-4 max-h-[220px] overflow-y-auto pr-1">
            {(activeTab === 'all' || activeTab === 'doc') && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-on-surface mb-2">
                  <Code className="h-3.5 w-3.5 text-primary" />
                  <span>Document & Employee Fields</span>
                </div>
                {displayedDocTokens.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic py-1">No matching document tokens.</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {displayedDocTokens.map((t) => (
                      <TokenChip
                        key={t.token}
                        token={t.token}
                        label={t.label}
                        example={t.example}
                        onInsert={() => handleInsert(t.token)}
                        onCopy={(e) => handleCopy(e, t.token)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {(activeTab === 'all' || activeTab === 'branding') && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-on-surface mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
                  <span>Company Legal & Branding Fields</span>
                </div>
                {displayedBrandingTokens.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic py-1">No matching branding tokens.</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {displayedBrandingTokens.map((t) => (
                      <TokenChip
                        key={t.token}
                        token={t.token}
                        label={t.label}
                        example={t.example}
                        isBranding
                        onInsert={() => handleInsert(t.token)}
                        onCopy={(e) => handleCopy(e, t.token)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t border-outline/30 pt-2">
            <span className="flex items-center gap-1">
              <Info className="h-3.5 w-3.5 text-primary" />
              <span>Hover over a token badge to preview example value. Click badge to insert at cursor.</span>
            </span>
            <span>Handlebars format: <code>{`{{token_name}}`}</code></span>
          </div>
        </div>
      )}
    </div>
  );
}

function TokenChip({
  token,
  label,
  example,
  isBranding = false,
  onInsert,
  onCopy,
}: {
  token: string;
  label: string;
  example?: string;
  isBranding?: boolean;
  onInsert: () => void;
  onCopy: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      onClick={onInsert}
      title={`Token: {{${token}}}\nExample: ${example || label}\nClick to insert into template`}
      className={`group relative inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-mono transition-all cursor-pointer select-none ${
        isBranding
          ? 'border-indigo-200 bg-indigo-50/70 text-indigo-900 hover:border-indigo-400 hover:bg-indigo-100/90 dark:border-indigo-900/60 dark:bg-indigo-950/40 dark:text-indigo-200'
          : 'border-outline/70 bg-surface text-on-surface hover:border-primary hover:bg-primary/5 hover:text-primary'
      }`}
    >
      <Plus className="h-3 w-3 opacity-60 group-hover:opacity-100 transition-opacity" />
      <span className="font-semibold">{`{{${token}}}`}</span>
      <span className="text-[10px] font-sans opacity-70 border-l border-outline/50 pl-1.5 truncate max-w-[120px]">
        {label}
      </span>
      <button
        type="button"
        onClick={onCopy}
        className="ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition-opacity p-0.5 rounded"
        title="Copy token Handlebars code"
      >
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}
